/* global document */
import queryString from 'querystring';
import { t } from '@lingui/macro';
import { find } from 'lodash';
import { DEFAULT_PRODUCT_ID } from './enums';

export function handleDefaults(defaultObj, dynamicObj) {
    for (const key in dynamicObj) {
        defaultObj[key] = dynamicObj[key];
    }
    return defaultObj;
}

export function log() {}

export function stripCharacters(currentString, characterToFind = ' ') {
    return currentString.split(characterToFind).join('');
}

export function actionCreator(type, payload) {
    return {
        type,
        payload
    };
}

export function getParam(param) {
    const queryParams = window.location.search;
    const query = queryParams.replace('?', '');
    const params = queryString.parse(query);
    return params[param];
}

export function resetGroupSectionScroll() {
    const groupRefElement = document.getElementsByClassName('groups__filters');
    if (groupRefElement.length > 0) {
        // As the focus of the group sets to newGroup or anything else, the scrollTop changes, as such reset them to 0
        groupRefElement[0].scrollTop = 0;
    }
}

export function getCorrectErrorMessage(response, i18n) {
    const { status, message, code } = response;

    if (status === 409) {
        // TODO: If backend has localisation this should possibly be considered to be implemented differently
        if (code === 'DuplicateEntityFound') {
            return i18n._(t`Error Message - Duplicate Name`);
        }
    }

    if (status === 400) {
        // TODO: If backend has localisation this should possibly be considered to be implemented differently
        if (code === 'Validation') {
            return i18n._(t`Error Message - Minmax password`);
        }
    }

    // If we don't support the error, return whatever message is being sent
    return message;
}

export function compareByLastName(firstStudent, secondStudent, isAscending) {
    const firstName = `${firstStudent.lastName.toLowerCase()} ${firstStudent.firstName.toLowerCase()}}`;
    const secondName = `${secondStudent.lastName.toLowerCase()} ${secondStudent.firstName.toLowerCase()}}`;

    if (firstName < secondName) {
        return isAscending ? 1 : -1;
    }
    if (firstName > secondName) {
        return isAscending ? -1 : 1;
    }
    return 0;
}

/**
 * This function sorts an array of object by ascending order of the key provided, by default.
 * @param key           The object key to sort on (No test about its existence)
 * @param isAscending   Setting this to false requests a descending order
 * @returns {function(*, *): number}
 * Usage:               const mySortedArray = myArray.sort(compareValues('myObjectKey', true);
 * Source:              https://www.sitepoint.com/sort-an-array-of-objects-in-javascript/#creatingadynamicsortingfunction
 */
export function compareValues(key, isAscending = true) {
    return function innerSort(a, b) {
        let comparison = 0;
        if (a[key].toLowerCase() > b[key].toLowerCase()) {
            comparison = 1;
        } else if (a[key].toLowerCase() < b[key].toLowerCase()) {
            comparison = -1;
        }
        return (
            isAscending ? comparison : (comparison * -1)
        );
    };
}
export function getDefaultGroupName(name) {
    return name.substring(0, name.length - 2);
}

/**
 * This function gets the value attributed to body::before in /styles/utils.scss, depending on the width of the screen
 * Not using CSS custom properties to support IE11.
 * @returns {string}    Possible values: ['tablet-portrait', 'tablet-landscape', 'laptop']
 *
 * https://andy-bell.design/wrote/get-css-custom-property-value-with-javascript/
 *
 * TODO: use custom properties as soon as IE is dropped and minimum browser is Edge 15.
 */
export function getScreenMode() {
    // eslint-disable-next-line no-undef
    return window.getComputedStyle(document.querySelector('body'), ':before')
        .getPropertyValue('content').replace(/"/g, '');
}

/**
 * A function to merge keys from a source collection with the target collection based on a shared id
 * @param targetCollection
 * @param sourceCollection
 * @param key in second collection to merge into the first
 * @param id to match objects in the two collections
 * @returns {[]}
 */
export function mergeKeyWithCollection(targetCollection, sourceCollection, key, id) {
    let mergeItem;
    return targetCollection.map((item) => {
        mergeItem = find(sourceCollection, [id, item[id]]);
        // Our eslint rules prohibit the use of 'hasOwnProperty'
        if (mergeItem && key in mergeItem) {
            mergeItem = mergeItem[key];
        }
        return Object.assign({}, item, { [key]: mergeItem });
    });
}

export function getLocale(localeOverride) {
    const DEFAULT_LOCALE = 'en-AU';
    return localeOverride || getParam('locale') || DEFAULT_LOCALE;
}

/**
 * This parses the config keys to find keys that look like "ProductName_ThemeCssUrl", and if found adds a key in the
 * config: { ThemeCssUrl: "Value of ProductName_ThemeCssUrl for the current product" }
 * If the productId provided is not listed or has no config value, defaults to DEFAULT_PRODUCT_ID config (Mathletics)
 * @param config        the already populated config
 * @param productId     the productId of the current product (should come from the Url)
 * BA 2020-04-24
 */
export function getProductById(config, productId) {
    const suffix = 'ThemeCssUrl';
    const separator = '_';
    /**
     * This dictionary MUST contain the product to be supported, otherwise theming will default to supplied default:
     * DEFAULT_PRODUCT_ID (which shoul;d be: 1 (Mathletics))
     * BA 2020-04-24
     */
    const productIdDictionary = {
        1: 'Mathletics',
        3: 'Spellodrome',
        8: 'Phonics',
        9: 'Mathletica',
        10: 'ReadiwriterSpelling',
        11: 'ReadiwriterWriting'
    };
    const dictionary = {};

    for (const configItem in config) {
        const [productName, theme] = configItem.split(separator);
        if (theme === suffix && productName === productIdDictionary[productId]) {
            dictionary[theme] = config[configItem];
        }
    }

    if (!dictionary.ThemeCssUrl) {
        dictionary.ThemeCssUrl = config[`${productIdDictionary[DEFAULT_PRODUCT_ID]}${separator}${suffix}`];
    }
    return {
        ...config,
        ...dictionary
    };
}

const getUserCountryCode = () => {
    const { countryCode } = queryString.parse(window.location.search);
    return countryCode ? countryCode.toUpperCase() : '';
};

// Determine if new courses are available for the selected country & year group in the group modal
// New courses are displayed in the group modal drop down list (DDL) (in edit and new group modals)
// New Courses are available only for AUS schools years 3-6 initially.
// All year grades DDL option has a value of -1
export const areNewCoursesAvailable = (yearGroup = '', countryId = '') => {
    // eslint-disable-next-line no-underscore-dangle
    const config = window._env_ || {};
    const newCoursesAvailability = config.newCoursesAvailabilityCriteria ? config.newCoursesAvailabilityCriteria : {};
    const userCountryCode = countryId || getUserCountryCode();

    return newCoursesAvailability[userCountryCode] && newCoursesAvailability[userCountryCode].yearGroups.includes(parseInt(yearGroup, 10));
};


export function getConfiguration(name) {
    // eslint-disable-next-line no-underscore-dangle
    if (window._env_) {
        // eslint-disable-next-line no-underscore-dangle
        return window._env_[name];
    }

    return '';
}

export function isValidMarketRegion() {
    const marketRegionId = getParam('marketRegionId') || '';

    const hiddenThreeEssentialsRegions = ['AMER'];

    return !hiddenThreeEssentialsRegions.includes(marketRegionId);
}



// WEBPACK FOOTER //
// ./helpers/utils.js